import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

const VendorPendingApprovalItemsPage = () => {
  const [pendingItems, setPendingItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { callId, type } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPendingItems = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No token found in local storage");
        }

        const response = await fetch(
          `${process.env.REACT_APP_CSH_BASE_URL}/api/v1/getPendingApprovalItems/${callId}/${type}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch pending items");
        }

        const data = await response.json();
        setPendingItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingItems();
  }, [callId, type]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found in local storage");
      }

      const decisions = Object.entries(selectedItems).map(([itemId, data]) => ({
        itemId,
        decision: data.status === "Approved" ? "approve" : "reject",
        comment: data.comment,
      }));

      const response = await fetch(
        `${process.env.REACT_APP_CSH_BASE_URL}/api/v1/submitVendorApproval`,
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ decisions }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit decisions");
      }

      // Refresh the page or update the list
      navigate(-1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500 dark:border-blue-300"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 dark:text-red-400">{error}</div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <header className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="bg-blue-500 hover:bg-blue-700 text-white mb-4 font-bold py-2 px-4 rounded dark:bg-gray-600 dark:hover:bg-gray-700"
        >
          Back
        </button>
        <h1 className="text-3xl font-bold mb-4">Pending Approval Items</h1>
        {pendingItems && pendingItems.length > 0 && (
          <div className="flex items-center gap-2 text-lg">
            <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-lg font-medium">
              {pendingItems[0].callCode}
            </span>
            <span className="text-gray-600 dark:text-gray-400 font-medium">
              {pendingItems[0].type}
            </span>
          </div>
        )}
      </header>

      <div className="flex gap-2 mt-4 mb-4">
        <button
          className="bg-green-500 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm font-medium"
          onClick={() => {
            const updatedItems = {};
            pendingItems.forEach((item) => {
              updatedItems[item.id] = {
                ...selectedItems[item.id],
                status: "Approved",
              };
            });
            setSelectedItems((prev) => ({
              ...prev,
              ...updatedItems,
            }));
          }}
        >
          Approve All
        </button>
        <button
          className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-sm font-medium"
          onClick={() => {
            const updatedItems = {};
            pendingItems.forEach((item) => {
              updatedItems[item.id] = {
                ...selectedItems[item.id],
                status: "Rejected",
              };
            });
            setSelectedItems((prev) => ({
              ...prev,
              ...updatedItems,
            }));
          }}
        >
          Reject All
        </button>
      </div>

      <div className="overflow-x-auto relative shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                Approval
              </th>
              <th scope="col" className="py-3 px-6">
                User Comment
              </th>
              <th scope="col" className="py-3 px-6">
                Resource
              </th>
              <th scope="col" className="py-3 px-6">
                Start Date
              </th>
              <th scope="col" className="py-3 px-6">
                End Date
              </th>
              <th scope="col" className="py-3 px-6">
                Count
              </th>
              <th scope="col" className="py-3 px-6">
                Status
              </th>
              <th scope="col" className="py-3 px-6">
                Comments
              </th>
              <th scope="col" className="py-3 px-6">
                Attachments
              </th>
            </tr>
          </thead>
          <tbody>
            {pendingItems && pendingItems.length > 0 ? (
              pendingItems.map((item) => (
                <tr
                  key={item.id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                >
                  <td className="py-4 px-6">
                    <div className="flex flex-col gap-2">
                      <button
                        className={`px-3 py-1 rounded-lg font-medium text-sm ${
                          selectedItems[item.id]?.status === "Approved"
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              status:
                                prev[item.id]?.status === "Approved"
                                  ? ""
                                  : "Approved",
                            },
                          }))
                        }
                      >
                        Approve
                      </button>
                      <button
                        className={`px-3 py-1 rounded-lg font-medium text-sm ${
                          selectedItems[item.id]?.status === "Rejected"
                            ? "bg-red-500 text-white"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              status:
                                prev[item.id]?.status === "Rejected"
                                  ? ""
                                  : "Rejected",
                            },
                          }))
                        }
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <input
                      type="text"
                      className={`bg-gray-50 border ${
                        selectedItems[item.id]?.status === "Rejected" &&
                        (selectedItems[item.id]?.comment === undefined ||
                          selectedItems[item.id]?.comment === "")
                          ? "border-red-500"
                          : "border-gray-300 dark:border-gray-600"
                      } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white`}
                      value={selectedItems[item.id]?.comment || ""}
                      onChange={(e) =>
                        setSelectedItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            comment: e.target.value,
                          },
                        }))
                      }
                      required={selectedItems[item.id]?.status === "Rejected"}
                      placeholder={
                        selectedItems[item.id]?.status === "Rejected"
                          ? "Required for rejection"
                          : "Optional comment"
                      }
                    />
                  </td>
                  <td className="py-4 px-6">{item.resources}</td>
                  <td className="py-4 px-6">{item.startDate}</td>
                  <td className="py-4 px-6">{item.endDate}</td>
                  <td className="py-4 px-6">{item.count}</td>
                  <td className="py-4 px-6">
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300">
                      {item.status}
                    </span>
                  </td>
                  <td className="py-4 px-6">{item.comments}</td>
                  <td className="py-4 px-6">
                    {item.attachments && item.attachments.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {item.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            onClick={async (e) => {
                              e.preventDefault();
                              // Use backend proxy for SharePoint file downloads
                              if (attachment.downloadUrl) {
                                try {
                                  const token = localStorage.getItem("token");
                                  const proxyUrl = `${process.env.REACT_APP_CSH_BASE_URL}/api/v1/download-attachment?url=${encodeURIComponent(attachment.downloadUrl)}`;
                                  
                                  // Fetch the file through the proxy
                                  const response = await fetch(proxyUrl, {
                                    method: 'GET',
                                    headers: {
                                      'Authorization': token,
                                    },
                                  });

                                  if (!response.ok) {
                                    throw new Error(`Download failed: ${response.statusText}`);
                                  }

                                  // Get the blob and create download link
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = attachment.name;
                                  document.body.appendChild(link);
                                  link.click();
                                  
                                  // Cleanup
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Download failed:', error);
                                  alert('Failed to download file. Please try again.');
                                }
                              }
                            }}
                            href="#"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm underline flex items-center gap-1 cursor-pointer"
                            title={`${attachment.name}${attachment.size > 0 ? ` (${Math.round(attachment.size / 1024)} KB)` : ''}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="truncate max-w-32">{attachment.name}</span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400 text-sm">No attachments</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                <td colSpan="9" className="py-4 px-6 text-center">
                  No pending items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-4 justify-start">
        <button
          onClick={() => setShowConfirmation(true)}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          disabled={
            Object.keys(selectedItems).length === 0 ||
            Object.values(selectedItems).some(
              (item) =>
                item.status === "Rejected" &&
                (!item.comment || item.comment.trim() === "")
            )
          }
        >
          Submit Decisions
        </button>
        <button
          onClick={() => setSelectedItems({})}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          Clear All
        </button>
      </div>
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4 dark:text-white">
              Confirm Decisions
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to submit the following decisions?
              <br />
              <br />
              Approved:{" "}
              {
                Object.values(selectedItems).filter(
                  (v) => v.status === "Approved"
                ).length
              }{" "}
              items
              <br />
              Rejected:{" "}
              {
                Object.values(selectedItems).filter(
                  (v) => v.status === "Rejected"
                ).length
              }{" "}
              items
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  handleSubmit(true);
                }}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorPendingApprovalItemsPage;
